import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { HeroBlock } from "@/components/HeroBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { CreateFormAction } from "@/components/forms/CreateFormAction.tsx";
import { IconRadio, type IconRadioContentProps } from "@/components/forms/IconRadio.tsx";
import { MonthlyCostField } from "@/components/forms/MonthlyCostField.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { LoadingSuspense } from "@/components/loading/Loading.tsx";
import { getResourceTypeName } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { BillingAccountField } from "@/modules/billing/forms/BillingAccountField.tsx";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getBucketPricePerGB } from "@/modules/pricing/resourcePricing.ts";
import { createStorageAccountMutation } from "@/modules/storage/account/storageAccountApi.ts";
import { storageAccountLink } from "@/modules/storage/links.ts";
import { cn } from "@/utils/classNames.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useControlledState } from "@react-stately/utils";
import { Navigate } from "@tanstack/react-router";
import type { StorageAccountCreateBody } from "@warrenio/api-spec/spec.oats.gen";
import { findById } from "@warrenio/utils/collections/findById";
import { unreachableSwitchCase } from "@warrenio/utils/unreachable";
import { Suspense, type ReactNode } from "react";
import { Input } from "react-aria-components";
import { useForm, useFormContext, type SubmitHandler } from "react-hook-form";

interface ItemType extends IconRadioContentProps {
    id: StorageFormType | StorageFormClass;
    description: ReactNode;
}

type StorageFormType = "storage_account";
type StorageFormClass = "standard_storage";

const types = [
    {
        id: "storage_account",
        title: getResourceTypeName("storage_account"),
        icon: "jp-archive-icon",
        description: undefined,
    },
] as const satisfies ItemType[];

/* const classes = [
    {
        id: "standard_storage",
        title: "Standard storage (S3)",
        icon: "jp-archive-icon",
        description: undefined,
    },
] as const satisfies ItemType[]; */

export interface CreateProps {
    typeId?: StorageFormType;
    onChangeType?: (type: StorageFormType) => void;
    //classId?: StorageFormClass;
    //onChangeClass?: (type: StorageFormClass) => void;
}

function StorageForm({
    typeId: controlledTypeId,
    onChangeType,
    //classId: controlledClassId,
    //onChangeClass,
}: CreateProps) {
    const { t } = useLingui();
    const [typeId, setType] = useControlledState(controlledTypeId, types[0].id, onChangeType);
    //const [classId, setClass] = useControlledState(controlledClassId, classes[0].id, onChangeClass);

    const type = findById(types, typeId ?? "storage_account"); // TODO: Only 1 choice at the moment
    //const storageClass = findById(classes, classId ?? "standard_storage");

    return (
        <>
            <WRadioGroup label={t`Type`} isRequired value={typeId} onChange={setType} description={type.description}>
                {types.map((item) => (
                    <IconRadio key={item.id} {...item} />
                ))}
            </WRadioGroup>

            <Separator />

            {/*<WRadioGroup
                label={t`Storage class`}
                isRequired
                value={classId}
                onChange={setClass}
                description={storageClass.description}
            >
                {classes.map((item) => (
                    <IconRadio wide key={item.id} {...item} />
                ))}
            </WRadioGroup>

            <Separator />*/}

            <LoadingSuspense>
                {typeId === "storage_account" ? <ObjectStorageCreateForm /> : unreachableSwitchCase(typeId)}
            </LoadingSuspense>
        </>
    );
}

interface ObjectStorageCreateInputs extends StorageAccountCreateBody {}

function ObjectStorageCreateForm({ inputs }: { inputs?: Partial<ObjectStorageCreateInputs> }) {
    const objectStorageMutation = useStandardMutation(createStorageAccountMutation);
    const form = useForm<ObjectStorageCreateInputs>({
        disabled: objectStorageMutation.isPending,
        defaultValues: {
            ...inputs,
        },
    });

    const onSubmit: SubmitHandler<ObjectStorageCreateInputs> = async ({ ...body }) => {
        await objectStorageMutation.mutateAsync({ body });
    };

    if (objectStorageMutation.isSuccess) {
        return <Navigate {...storageAccountLink(objectStorageMutation.data)} />;
    }

    return (
        <WHookForm form={form} onSubmit={onSubmit}>
            <StorageNameField isRequired />

            <Separator />

            <Suspense>
                <ObjectStorageMonthlyCostField billingAccountId={form.watch("billing_account_id")} />
            </Suspense>

            <BillingAccountField resourceType="bucket" isRequired />
            <Separator />

            <CreateFormAction resourceType="bucket" />
        </WHookForm>
    );
}

function ObjectStorageMonthlyCostField({ billingAccountId }: { billingAccountId?: number }) {
    const prices = useSuspenseQueryAtom(pricesAtom);

    return (
        <MonthlyCostField
            price={getBucketPricePerGB(prices)}
            billingAccountId={billingAccountId}
            priceDescription={
                <Trans>
                    Actual cost of your storage is calculated hourly based on your actual usage, no upfront payments.
                    For every started GB the price is rounded up to next whole GB.
                </Trans>
            }
        />
    );
}

// function

function StorageNameField(textFieldProps: WTextFieldProps) {
    const { t } = useLingui();
    const form = useFormContext<ObjectStorageCreateInputs>();
    const { ref, props } = useAriaField("title", form, {
        isRequired: true,
    });
    return (
        <WTextField
            {...props}
            label={t`Display name`}
            description={t`This will only be used within the system so it's easy for you to recognize the account if you have several.`}
            {...textFieldProps}
        >
            <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

export function StorageCreate(props: CreateProps) {
    return (
        <>
            <CreateHero />
            <StorageForm {...props} />
        </>
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-stack-icon">
            <Trans>
                S3-compatible object storage allows you to scale your storage resources up and down without any upfront
                payments. Create a new storage account, create some buckets and start storing your data in a scalable
                way - Pay-as-you-grow.
            </Trans>
        </HeroBlock>
    );
}
